/**
 * TurnHistory — chronologický seznam turnů v session.
 *
 * Pro každý turn vykreslí:
 *  - user/assistant/system bublinu s textem (markdown)
 *  - pokud má diff_unified → DiffViewer (s Apply/Revert tlačítky)
 *  - revertované turny jsou přeškrtnuté (Lovable způsob)
 */
import ReactMarkdown from 'react-markdown';
import type { ComponentType } from 'react';
import type { Turn } from './DevstudioWorkspace';
import { useTranslation } from 'react-i18next';

interface DiffViewerProps {
  turn: Turn;
  onApply: (turnId: string) => void | Promise<void>;
  onRevert: (turnId: string) => void | Promise<void>;
}

interface Props {
  turns: Turn[];
  onApply: (turnId: string) => void | Promise<void>;
  onRevert: (turnId: string) => void | Promise<void>;
  DiffViewer: ComponentType<DiffViewerProps>;
}

export function TurnHistory({ turns, onApply, onRevert, DiffViewer }: Props) {
  const { t } = useTranslation();
  if (turns.length === 0) {
    return (
      <div className="text-sm opacity-50 text-center py-6">
        {t('devstudio.zadneTurnyNapisPrvni', 'Žádné turny — napiš první zprávu níže.')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {turns.map((t) => {
        const isReverted = t.state === 'reverted';
        const bubbleCls = t.role === 'user'
          ? 'bg-zinc-800 ml-8'
          : t.role === 'system'
          ? 'bg-zinc-900 border border-zinc-800 italic opacity-70'
          : 'bg-zinc-950 border border-zinc-800 mr-8';

        // Pokud je assistant turn s diffem — text bez diff bloku zobraz, diff dej zvlášť
        let textWithoutDiff = t.content;
        if (t.diff_unified && t.role === 'assistant') {
          textWithoutDiff = t.content.replace(/```diff\n[\s\S]*?```/, '').trim();
        }

        return (
          <div key={t.id} className={isReverted ? 'opacity-50' : ''}>
            {/* Text bublina */}
            {textWithoutDiff && (
              <div className={`text-sm rounded p-2 ${bubbleCls}`}>
                <div className="text-[10px] uppercase opacity-50 mb-1">
                  #{t.turn_index} {t.role}
                  {isReverted && <span className="ml-1 line-through">reverted</span>}
                </div>
                <div className={isReverted ? 'line-through' : ''}>
                  <ReactMarkdown>{textWithoutDiff}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Diff (pokud existuje) */}
            {t.diff_unified && (
              <div className="mt-1 mr-8">
                <DiffViewer turn={t} onApply={onApply} onRevert={onRevert} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
